import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { SERVICES, LOGO_URL } from "@/lib/content";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const servicesActive = location.pathname.startsWith("/services");

  // Nav link colour depends on scroll state
  const linkClass = ({ isActive }) => {
    if (!scrolled) {
      return `text-[13px] font-medium tracking-wide drop-shadow-[0_1px_6px_rgba(0,0,0,0.35)] transition-colors ${
        isActive ? "text-white" : "text-white/85 hover:text-white"
      }`;
    }
    return `text-[13px] font-medium tracking-wide transition-colors ${
      isActive ? "text-sage-700" : "text-ink-soft hover:text-sage-700"
    }`;
  };

  const servicesBtnClass = () => {
    if (!scrolled) {
      return `flex items-center gap-1.5 text-[13px] font-medium tracking-wide drop-shadow-[0_1px_6px_rgba(0,0,0,0.35)] transition-colors ${
        servicesActive ? "text-white" : "text-white/85 hover:text-white"
      }`;
    }
    return `flex items-center gap-1.5 text-[13px] font-medium tracking-wide transition-colors ${
      servicesActive ? "text-sage-700" : "text-ink-soft hover:text-sage-700"
    }`;
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-sage-100 bg-white/92 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div
        className={`container-narrow flex items-center justify-between transition-all duration-500 ${
          scrolled ? "h-32 md:h-36" : "h-40 md:h-52"
        }`}
      >
        <Link
          to="/"
          data-testid="logo-home-link"
          className="flex items-center gap-3"
          aria-label="Bellhouse Law, Home"
        >
          <img
            src={LOGO_URL}
            alt="Bellhouse Law"
            className={`w-auto transition-all duration-500 ${
              scrolled ? "h-28 md:h-32" : "h-32 md:h-48"
            } ${
              scrolled
                ? ""
                : "brightness-0 invert drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)]"
            }`}
          />
        </Link>

        <nav
          className="hidden items-center gap-9 lg:flex"
          aria-label="Primary"
        >
          <NavLink
            to="/"
            end
            data-testid="nav-link-home"
            className={linkClass}
          >
            Home
          </NavLink>

          <NavLink
            to="/about"
            data-testid="nav-link-about"
            className={linkClass}
          >
            About
          </NavLink>

          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-trigger"
              onClick={() => setServicesOpen((v) => !v)}
              className={servicesBtnClass()}
            >
              Services
              <ChevronDown
                className={`h-3 w-3 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                strokeWidth={1.5}
              />
            </button>
            {servicesOpen && (
              <div
                data-testid="nav-services-menu"
                className="absolute left-1/2 top-full z-50 w-80 -translate-x-1/2 pt-3"
              >
                <div className="border border-sage-100 bg-white p-2 shadow-[0_24px_60px_-30px_rgba(26,30,25,0.25)]">
                  <Link
                    to="/services"
                    data-testid="nav-services-overview"
                    className="block px-4 py-3 text-[13px] font-medium text-ink hover:bg-sage-50"
                  >
                    All Services
                  </Link>
                  <div className="my-1 h-px bg-sage-100" />
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      className="block px-4 py-3 text-[13px] text-ink-soft hover:bg-sage-50 hover:text-sage-700"
                    >
                      {s.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/contact"
            data-testid="nav-link-contact"
            className={linkClass}
          >
            Contact
          </NavLink>
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <Link
            to="/contact"
            data-testid="header-enquire-cta"
            className={`whitespace-nowrap px-5 py-3 text-[11px] font-medium uppercase tracking-[0.18em] transition-all ${
              scrolled
                ? "bg-sage-600 text-white hover:bg-sage-700"
                : "border border-white/70 bg-white/10 text-white backdrop-blur-md hover:bg-white hover:text-ink"
            }`}
          >
            Make an Enquiry
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className={`grid h-10 w-10 place-items-center border lg:hidden ${
            scrolled
              ? "border-sage-200 text-ink"
              : "border-white/60 text-white"
          }`}
          onClick={() => setOpen((s) => !s)}
        >
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-sage-100 bg-white lg:hidden"
        >
          <div className="container-narrow flex flex-col py-6">
            {[
              { label: "Home", to: "/" },
              { label: "About", to: "/about" },
              { label: "Services", to: "/services" },
              ...SERVICES.map((s) => ({
                label: s.title,
                to: `/services/${s.slug}`,
              })),
              { label: "Contact", to: "/contact" },
            ].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s|&/g, "-")}`}
                className={({ isActive }) =>
                  `border-b border-sage-100 py-3 text-sm font-medium ${
                    isActive ? "text-sage-700" : "text-ink-soft"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-enquire-cta"
              className="btn-primary mt-6 w-full"
            >
              Make an Enquiry
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
